import TurndownService from "turndown";

/**
 * Turn the HTML flavour of a clipboard paste into markdown.
 *
 * Clipboard HTML is not a document. It is a fragment of somebody else's page:
 * spans nested four deep carrying inline styles, citation chips whose targets
 * the serialiser has already lost, tracking pixels, and the invisible
 * characters a rendering engine leaves behind. Everything here exists to get
 * from that to the markdown the person would have written by hand.
 */

// U+FFFC marks the spot where an embedded object was; U+FEFF and the
// zero-width family are rendering residue. None of them mean anything in a
// note, and all of them arrive only through a browser's HTML-to-text
// serialisation. Four U+FFFC in production are what proved that is the path.
const INVISIBLE = /[\u200B-\u200D\uFEFF\uFFFC]/g;

// Narrow, thin and non-breaking spaces all render as a space and match none of
// the patterns that expect one.
const ODD_SPACES = /[\u00A0\u2007\u2009\u202F]/g;

// The tags that carry structure worth recovering. Everything else on a
// clipboard is presentation: wrapper divs, spans holding inline styles, the
// stray <br>. If none of these are present, the browser's own plain-text paste
// already says everything the HTML does, and converting could only add noise.
//
// This deliberately reads the markup rather than pattern-matching the text. An
// earlier version sniffed the plain text for markdown-looking characters, which
// meant one quoted email line beginning "> " switched the whole feature off for
// that paste, with no signal to the person pasting.
const STRUCTURAL_TAGS =
  "h1,h2,h3,h4,h5,h6,strong,b,em,i,ul,ol,table,blockquote,pre,code,a[href]," +
  "s,del,strike,sup,sub,hr,math,mjx-container,.katex," +
  'input[type="checkbox"]';

function hasStructuralMarkup(html: string): boolean {
  // DOMParser builds an inert document: no requests are issued, no script runs.
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.querySelector(STRUCTURAL_TAGS) !== null;
}

// Turndown escapes every * and _ it finds in text, wherever they sit, so that
// nothing in the source can be mistaken for markdown syntax. On this content
// that is mostly damage. A meeting summary is full of file_name.ts and "3 * 4",
// neither of which means anything to a parser, and the backslashes would go
// into the column and appear the moment somebody clicks Edit. That edit view is
// what prompted this whole item.
//
// So those two are escaped only in the shape that could really parse as syntax.
// Everything else turndown escapes is kept exactly as it was, brackets very
// much included: escaping runs once per text node, so a rule that has to look
// past the node it is given cannot be trusted. `<p>[doc]<span>: url</span></p>`
// splits the bracket from the colon, and an unescaped pair there is read as a
// link reference definition, which renders as nothing at all. Brackets are
// tidied afterwards instead, on the assembled markdown, where no boundary is
// left to fall through.
const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c);
const isWord = (c: string | undefined) => c !== undefined && /\w/.test(c);

function escapeMarkdown(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/^-/, "\\-")
    .replace(/^\+ /, "\\+ ")
    .replace(/^(=+)/, "\\$1")
    .replace(/^(#{1,6}) /, "\\$1 ")
    .replace(/^~~~/, "\\~~~")
    .replace(/^>/, "\\>")
    .replace(/^(\d+)\. /, "$1\\. ")
    .replace(/`/g, "\\`")
    .replace(/\[/g, "\\[")
    .replace(/\]/g, "\\]")
    .replace(/[*_]/g, (ch: string, at: number, s: string) => {
      const before = s[at - 1];
      const after = s[at + 1];

      // A star opens or closes emphasis only when it touches a non-space, and
      // starts a bullet only at the head of a line. "3 * 4" is neither.
      if (ch === "*") {
        if (!isSpace(before) || !isSpace(after)) return "\\*";
        return at === 0 || before === "\n" ? "\\*" : ch;
      }

      // An underscore between two word characters never marks emphasis. That is
      // the rule that keeps snake_case readable, and it is worth relying on.
      return isWord(before) && isWord(after) ? ch : "\\_";
    });
}

// An escaped pair of brackets with nothing but ordinary text between them, and
// nothing after it that could turn it into syntax. [00:12:34] and [Decision]
// are the whole reason this exists: a timestamp is not a link, and two
// backslashes around it are two more than the note needs.
//
// Safe here in a way it would not be inside escapeMarkdown, because turndown
// has finished. A link it built itself reads `[text](url)` with the opening
// bracket unescaped, so this pattern cannot match one, and the lookahead keeps
// the escapes on anything followed by `(`, `[` or `:`, which are the three
// characters that make a bracket pair mean something.
const HARMLESS_BRACKETS = /\\\[([^\\[\]\n]*)\\\](?![([:])/g;

// A task item whose label the markup wrapped in a paragraph comes out with the
// checkbox alone on its line. marked only draws a checkbox with text beside
// it, so both boxes rendered as a literal "[x]". A nested list is left where it
// is: that is a box with no label, not a label on the wrong line.
const DETACHED_TASK_LABEL =
  /^((?:[ \t]*>)*[ \t]*[-*+] \[[ xX]\])\n+[ \t]+(?=\S)(?![-*+] |\d+\. )/gm;

let service: TurndownService | undefined;
let cellService: TurndownService | undefined;

/** The options and rules every conversion shares, table rule aside. */
function base(): TurndownService {
  const turndown = new TurndownService({
    headingStyle: "atx",
    bulletListMarker: "-",
    codeBlockStyle: "fenced",
    emDelimiter: "*",
    // <Markdown> is configured with breaks: true, so a bare newline is already
    // a line break. Turndown's default of two trailing spaces would be
    // invisible noise that every editor then strips back out.
    br: "\n",
  });

  turndown.escape = escapeMarkdown;

  turndown.remove(["style", "script", "head", "meta", "link", "noscript"]);

  // A citation chip is an anchor whose visible text is a source name and whose
  // href is either absent or a fragment into a page we cannot reach. Neither
  // should become a link. The words are still worth keeping.
  turndown.addRule("chipToText", {
    filter: (node) => {
      if (node.nodeName !== "A") return false;
      const href = node.getAttribute("href");
      return !href || href.startsWith("#") || !node.textContent?.trim();
    },
    replacement: (content) => content,
  });

  // No assistant ships a bare <pre><code>. ChatGPT wraps the block in a stack
  // of divs carrying a language label and a Copy code button, and a mouse
  // selection drags every one of them in. Turndown's own fence rule matches
  // only a <pre> whose first child is the <code>, so those blocks missed it and
  // fell through to the inline-code rule, which replaces each newline with a
  // space. Code arrived on a single line with the chrome still attached.
  //
  // Reading the <code> descendant instead skips the chrome, because the labels
  // and the button are its siblings rather than its content.
  turndown.addRule("codeBlock", {
    filter: "pre",
    replacement: (_content, node) => {
      // Not the first <code>: the chrome has one of its own on Perplexity and
      // some Gemini builds, holding the language name beside the Copy button.
      // Taking that one gives a block whose entire body is the word "python"
      // and drops the code. The tagged one is the answer when it exists, and
      // the longest is the honest guess when nothing is tagged.
      const blocks = Array.from((node as HTMLElement).querySelectorAll("code"));
      const code =
        blocks.find((c) => /(?:^|\s)(?:language|lang)-/.test(c.className)) ??
        blocks.reduce<Element | null>(
          (best, c) =>
            (c.textContent ?? "").length > (best?.textContent ?? "").length
              ? c
              : best,
          null,
        ) ??
        node;
      const text = (code.textContent ?? "").replace(/\n+$/, "");
      if (!text.trim()) return "";

      const language = /(?:^|\s)(?:language|lang)-(\S+)/.exec(
        (code as HTMLElement).className ?? "",
      )?.[1];

      // A fence has to outrun the longest run of backticks inside it.
      const longest = Math.max(
        2,
        ...Array.from(text.matchAll(/`+/g), (m) => m[0].length),
      );
      const fence = "`".repeat(longest + 1);

      return `\n\n${fence}${language ?? ""}\n${text}\n${fence}\n\n`;
    },
  });

  // Turndown drops the checkbox and keeps the words, so a completed action item
  // and an outstanding one come out as the same bullet. On an Action Items list
  // that is the whole payload. marked renders GFM task lists and DOMPurify
  // keeps the input, so the markdown round-trips to a real checkbox.
  turndown.addRule("taskListItem", {
    filter: (node) =>
      node.nodeName === "INPUT" &&
      (node as HTMLInputElement).type === "checkbox" &&
      (node as HTMLElement).closest("li") !== null,
    replacement: (_content, node) =>
      (node as HTMLInputElement).checked ? "[x] " : "[ ] ",
  });

  // Turndown's core has no strikethrough, so struck-out text arrived reading as
  // though it still stood. marked renders ~~ and this project's <Markdown> is
  // on marked's defaults, which include GFM.
  turndown.addRule("strikethrough", {
    // A predicate rather than a tag list: <strike> is old enough that the DOM
    // typings no longer name it, and Gmail quotes still carry it.
    filter: (node) => ["S", "DEL", "STRIKE"].includes(node.nodeName),
    replacement: (content) => (content.trim() ? `~~${content}~~` : content),
  });

  // Markdown has no superscript, and dropping the tag turns 2^10 into "210" and
  // H2O into "H2O": wrong, and wrong in a way nobody would spot in a note.
  // marked passes inline HTML through and DOMPurify keeps both tags, so writing
  // the tag is the one option that renders what was copied.
  turndown.addRule("supSub", {
    filter: ["sup", "sub"],
    replacement: (content, node) => {
      const tag = node.nodeName.toLowerCase();
      return content.trim() ? `<${tag}>${content}</${tag}>` : content;
    },
  });

  // A rendered equation is the same expression three times over: MathML for
  // screen readers, the LaTeX source in an annotation, and a tree of spans for
  // the visuals. Turndown reads all three and runs them together, so E = mc^2
  // arrived as "E\=mc2E = mc^2E\=mc2". Nothing here renders LaTeX, so the
  // source is the honest thing to keep, and keeping it once is the point.
  // Nothing more than that: every assistant puts maths on the clipboard in a
  // shape of its own, and chasing each one is not worth it for a CRM note.
  turndown.addRule("math", {
    filter: (node) =>
      node.nodeName === "MATH" ||
      node.nodeName === "MJX-CONTAINER" ||
      (node.nodeName === "SPAN" && node.classList.contains("katex")),
    replacement: (_content, node) => {
      const el = node as HTMLElement;
      const tex = el
        .querySelector('annotation[encoding="application/x-tex"]')
        ?.textContent?.trim();

      // No source to recover, so the rendered characters are all there is.
      // Read them off one branch rather than every branch.
      if (!tex)
        return (el.querySelector("math") ?? el).textContent?.trim() ?? "";

      return `$${tex}$`;
    },
  });

  // An image on a clipboard is either a data: URI, which would put megabytes of
  // base64 into a text column, or a URL only the page it came from can serve.
  // The alt text is the only part that survives the trip.
  turndown.addRule("imageToAltText", {
    filter: "img",
    replacement: (_content, node) =>
      (node as HTMLImageElement).getAttribute("alt")?.trim() ?? "",
  });

  return turndown;
}

/**
 * Converts the inside of one table cell.
 *
 * Separate from the main service, and without the table rule, so that a table
 * nested inside a cell flattens to its text instead of recursing. Nested tables
 * are vanishingly rare on a clipboard; an infinite loop would not be.
 */
function getCellService(): TurndownService {
  cellService ??= base();
  return cellService;
}

/** One cell's markdown, forced onto a single line. */
function cell(node: Element): string {
  return getCellService()
    .turndown(node.innerHTML)
    .replace(/\|/g, "\\|") // a literal pipe would end the cell
    .replace(/\s*\n+\s*/g, " ")
    .trim();
}

function getService(): TurndownService {
  if (service) return service;

  const turndown = base();

  // Turndown's core has no table support: without this a table arrives as its
  // cell values run together, one per line, with nothing to say which value
  // belongs to which column. That is the same collapse this whole module
  // exists to prevent, and chat assistants emit tables constantly.
  //
  // marked renders GFM tables and <Markdown> already styles them, so a markdown
  // table is all that is needed here.
  turndown.addRule("gfmTable", {
    filter: "table",
    replacement: (_content, node) => {
      const rows = Array.from((node as HTMLTableElement).rows);
      if (!rows.length) return "";

      const cells = (row: HTMLTableRowElement) =>
        Array.from(row.cells).map((c) => cell(c));

      // Markdown has no way to express a table without a header, so the first
      // row becomes one. Assistant output effectively always has a real header
      // row, which is what makes that the right guess rather than an empty one.
      const header = cells(rows[0]);
      const width = Math.max(...rows.map((r) => r.cells.length));
      const pad = (r: string[]) =>
        r.concat(Array(Math.max(0, width - r.length)).fill(""));
      const line = (r: string[]) => `| ${pad(r).join(" | ")} |`;

      // A right-aligned column of figures reads as one; left-aligned it does
      // not. marked turns these markers into the align attribute, and the
      // header row is where every assistant puts the alignment.
      const marker = (i: number) => {
        const c = rows[0].cells[i];
        const how = (
          c?.getAttribute("align") ??
          c?.style.textAlign ??
          ""
        ).toLowerCase();
        if (how === "center") return ":---:";
        if (how === "right") return "---:";
        return how === "left" ? ":---" : "---";
      };

      const out = [
        line(header),
        `| ${Array.from({ length: width }, (_, i) => marker(i)).join(" | ")} |`,
        ...rows.slice(1).map((r) => line(cells(r))),
      ];

      return `\n\n${out.join("\n")}\n\n`;
    },
  });

  service = turndown;
  return turndown;
}

function tidy(markdown: string): string {
  return (
    markdown
      .replace(INVISIBLE, "")
      .replace(ODD_SPACES, " ")
      .replace(/[ \t]+$/gm, "")
      // Turndown pads every list marker out to four characters. Nothing renders
      // that differently, but it is not what a person writes, and these fields
      // are edited by hand after they are pasted. The leading indent is kept so
      // nested lists still nest, and the blockquote markers so they stay quoted.
      .replace(/^((?:\s*>)*\s*)([-*+])[ \t]{2,}/gm, "$1$2 ")
      .replace(/^((?:\s*>)*\s*)(\d+\.)[ \t]{2,}/gm, "$1$2 ")
      // The markup nearly always puts a space after the checkbox, and the
      // marker this module writes already ends in one.
      .replace(/^((?:\s*>)*\s*[-*+] )\[([ xX])\][ \t]+/gm, "$1[$2] ")
      .replace(DETACHED_TASK_LABEL, "$1 ")
      .replace(HARMLESS_BRACKETS, "[$1]")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );
}

/**
 * Markdown recovered from the clipboard, or null to leave the paste alone.
 *
 * Null is the common answer and the safe one. It means the browser would paste
 * something at least as good as anything produced here, so the handler declines
 * to intervene rather than risk mangling it.
 *
 * There are exactly two reasons to decline: the clipboard carries no HTML at
 * all, which is what a chat assistant's own Copy button produces and is already
 * the raw markdown we want; or the HTML it carries has no structure in it, so
 * there is nothing to recover. Both are facts about the clipboard, not guesses
 * about what somebody meant.
 */
export function clipboardToMarkdown(html: string): string | null {
  if (!html.trim()) return null;
  if (!hasStructuralMarkup(html)) return null;

  try {
    return tidy(getService().turndown(html)) || null;
  } catch {
    // A paste must never fail. Returning null leaves the browser to do what it
    // would have done anyway.
    return null;
  }
}
